import { Button, Input, Select } from '@artosera/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { texteEnLangue } from '@artosera/shared';
import { useContacts } from '../../api/contacts.js';
import { useExpositions } from '../../api/expositions.js';
import { useArtistes } from '../../api/artistes.js';
import { useOeuvres } from '../../api/oeuvres.js';
import { useDebounce } from '../oeuvres/useDebounce.js';
import styles from './AgendaPage.module.css';
export type ReferenceAgenda = {
  type: 'contact' | 'exposition' | 'artiste' | 'oeuvre';
  id: string;
  nom?: string;
};
export function RechercheReference({
  galerieSlug,
  types,
  valeurs,
  onChanger,
}: {
  galerieSlug: string;
  types: ReferenceAgenda['type'][];
  valeurs: ReferenceAgenda[];
  onChanger: (v: ReferenceAgenda[]) => void;
}) {
  const { t, i18n } = useTranslation(['regie']);
  const [type, setType] = useState(types[0]!);
  const [q, setQ] = useState('');
  const cherche = useDebounce(q, 250);
  const contacts = useContacts(
    galerieSlug,
    { q: cherche, parPage: 8 },
    { enabled: Boolean(cherche) && type === 'contact' },
  );
  const expos = useExpositions(
    galerieSlug,
    { q: cherche, parPage: 8 },
    { enabled: Boolean(cherche) && type === 'exposition' },
  );
  const artistes = useArtistes(
    galerieSlug,
    { q: cherche, parPage: 8 },
    { enabled: Boolean(cherche) && type === 'artiste' },
  );
  const oeuvres = useOeuvres(
    galerieSlug,
    { q: cherche, parPage: 8 },
    { enabled: Boolean(cherche) && type === 'oeuvre' },
  );
  const resultats =
    type === 'contact'
      ? contacts.data?.contacts.map((c) => ({ id: c.id, nom: c.nomAffiche }))
      : type === 'exposition'
        ? expos.data?.expositions.map((e) => ({
            id: e.id,
            nom: texteEnLangue(e.titre, i18n.language === 'en' ? 'en' : 'fr'),
          }))
        : type === 'artiste'
          ? artistes.data?.artistes.map((a) => ({ id: a.id, nom: a.nom }))
          : oeuvres.data?.oeuvres.map((o) => ({ id: o.id, nom: o.titre }));
  return (
    <div className={styles.formulaire}>
      <Select
        aria-label={t('regie:agenda.reference.type')}
        value={type}
        onValueChange={(v) => {
          setType(v as ReferenceAgenda['type']);
          setQ('');
        }}
        options={types.map((v) => ({ value: v, label: t(`regie:agenda.reference.${v}`) }))}
      />
      <Input
        aria-label={t('regie:agenda.reference.chercher')}
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {cherche
        ? resultats?.map((r) => (
            <Button
              key={r.id}
              variant="ghost"
              onClick={() => {
                if (!valeurs.some((v) => v.id === r.id && v.type === type))
                  onChanger([...valeurs, { type, ...r }]);
                setQ('');
              }}
            >
              {r.nom}
            </Button>
          ))
        : null}
      <div className={styles.actions}>
        {valeurs.map((v) => (
          <Button
            key={`${v.type}:${v.id}`}
            variant="secondary"
            onClick={() => onChanger(valeurs.filter((r) => r !== v))}
          >
            {v.nom ?? t(`regie:agenda.reference.${v.type}`)} · {t('regie:agenda.reference.retirer')}
          </Button>
        ))}
      </div>
    </div>
  );
}
